import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconX } from '@tabler/icons-react';
import TextPanel from './panels/TextPanel';
import ElementsPanel from './panels/ElementsPanel';
import PhotosPanel from './panels/PhotosPanel';
import BrandKitPanel from './panels/BrandKitPanel';
import LayersPanel from './panels/LayersPanel';
import VideosPanel from './panels/VideosPanel';
import { enter, leave, useEnter } from '../../motion';
import './styles.scss';

const PANELS = { text: TextPanel, elements: ElementsPanel, photos: PhotosPanel, videos: VideosPanel, brand: BrandKitPanel, layers: LayersPanel };
const TITLES = { text: 'toolText', elements: 'toolElements', photos: 'toolPhotos', videos: 'toolVideos', brand: 'toolBrand', layers: 'toolLayers' };

// Pressing anywhere else closes the panel; the rail (its own toggles) and the floating
// pickers a panel opens (portaled to the body) count as inside.
const INSIDE = '.art-panel, .art-tools, .art-popover, .custom-popper-portal';

// The panel of the open tool, floating over the stage's left side, or with `placement`
// "outside" left of the rail (over what the host has there). It slides in and out
// (GSAP, transform + opacity); switching tools while open only fades its content. While
// closing it keeps the last tool's content so the exit is not empty. A pick that lands in
// the art (a text, an element, a photo) closes it too, from the panel itself.
export default function ToolPanel({ tool, mode, placement = 'stage', onClose }) {
  const { t } = useTranslation('artEditor');
  const ref = useRef(null);
  const last = useRef(tool);
  if (tool) last.current = tool;
  const open = !!tool;
  const close = useRef(onClose);
  close.current = onClose;
  useLayoutEffect(() => {
    // It slides out of the rail: rightwards over the stage, leftwards when it opens outside.
    const from = { x: placement === 'outside' ? 12 : -12 };
    if (open) enter(ref.current, from, { duration: 0.2 });
    else leave(ref.current, from);
  }, [open, placement]);
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = event => { if (!event.target.closest?.(INSIDE)) close.current(); };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);
  const body = useEnter(`${tool}-${mode || ''}`, { y: 4 }, { duration: 0.16 });
  const Panel = PANELS[last.current];
  const title = mode === 'replace' ? 'replaceImage' : mode === 'replaceIcon' ? 'replaceIcon' : mode === 'fill' ? 'fillWithImage' : TITLES[last.current];
  return <aside id="art-editor-panel" ref={ref} className={`art-panel is-${placement}`} aria-hidden={!open} inert={open ? undefined : ''} aria-label={title ? t(title) : undefined}>
    {Panel && <>
      <header className="art-panel__header">
        <Text.h6 weight={500} contrast={9}>{t(title)}</Text.h6>
        <Button size="small" variant="text" color="overlay" preventLoading icon={<IconX />} aria-label={t('closePanel')} tooltip={t('closePanel')} onClick={onClose} />
      </header>
      <div ref={body} className="art-panel__body"><Panel mode={mode} onClose={onClose} /></div>
    </>}
  </aside>;
}
